import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./DataView.css";

/* ── Alertes ── */
function AlertsList({ alerts, t }) {
  if (!alerts?.length)
    return <div className="cb-empty">{t("cycleBot.dataView.noAlerts")} ✅</div>;

  return (
    <div className="cb-alerts-list">
      {alerts.map((a, i) => {
        const isCrit = a.level === "critique" || a.level === "critical";
        return (
          <div key={i} className={`cb-alert-card ${isCrit ? "crit" : ""}`}>
            <div className="cb-alert-hd">
              <span className="cb-alert-icon">{isCrit ? "🚨" : "⚠️"}</span>
              <span className="cb-alert-title">{a.message}</span>
              <span className="cb-alert-badge">{a.level}</span>
            </div>
            {a.details && <div className="cb-alert-detail">{a.details}</div>}
            {a.recommendations?.length > 0 && (
              <ul className="cb-alert-recs">
                {a.recommendations.map((r, j) => <li key={j}>{r}</li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Cycles ── */
function CycleCard({ c, index, t, locale }) {
  const isNormal    = c.status === "normal";
  const isPredicted = c.status === "predicted";

  // Formate une date lisible
  const fmt = (d) => {
    if (!d) return "--";
    try { return new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  const rows = [
    { lbl: t("cycleBot.dataView.start"),      val: fmt(c.start_date) },
    { lbl: t("cycleBot.dataView.periodEnd"),  val: fmt(c.end_date) },
    { lbl: t("cycleBot.dataView.duration"),   val: c.cycle_length  || "--" },
    { lbl: t("cycleBot.dataView.bleeding"),   val: c.bleeding_days || "--" },
  ];
  if (c.medical_note) rows.push({ lbl: t("cycleBot.dataView.note"), val: c.medical_note });

  return (
    <div className="cb-cycle-card">
      <div className="cb-cycle-top">
        <span className="cb-cycle-num">{t("cycleBot.dataView.cycleNumber", { number: c.cycle_number || index + 1 })}</span>
        <span className={`cb-cycle-badge ${!isNormal ? (isPredicted ? "predicted" : "abnormal") : ""}`}>
          {isNormal
            ? t("cycleBot.dataView.statusNormal")
            : isPredicted
              ? t("cycleBot.dataView.statusPredicted")
              : t("cycleBot.dataView.statusAtypical")}
        </span>
      </div>
      {rows.map((row) => (
        <div key={row.lbl} className="cb-cycle-row">
          <span className="cb-cycle-lbl">{row.lbl}</span>
          <span className="cb-cycle-val">{row.val}</span>
        </div>
      ))}
    </div>
  );
}

function CyclesList({ cycles, t, locale }) {
  if (!cycles?.length)
    return <div className="cb-empty">{t("cycleBot.dataView.noCycles")}</div>;

  return (
    <div className="cb-cycles-grid">
      {cycles.map((c, i) => <CycleCard key={i} c={c} index={i} t={t} locale={locale} />)}
    </div>
  );
}

/* ── Recherche ── */
function SearchPanel({ onSearch, results, t, locale }) {
  const [date, setDate] = useState("");

  return (
    <>
      <div className="cb-search-row">
        <input
          type="month"
          className="cb-search-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <button className="cb-btn-primary" onClick={() => date && onSearch(date)}>
          🔍 {t("cycleBot.dataView.search")}
        </button>
      </div>
      {results
        ? <CyclesList cycles={results} t={t} locale={locale} />
        : <div className="cb-empty">{t("cycleBot.dataView.selectMonth")}</div>
      }
    </>
  );
}

/* ── Vue principale ── */
export default function DataView({ alerts, cycles, onSearch, searchResults }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const [tab, setTab] = useState("alerts");
  const tabs = [
    { key: "alerts", label: `⚠️ ${t("cycleBot.dataView.tabAlerts")}` },
    { key: "cycles", label: `🔄 ${t("cycleBot.dataView.tabCycles")}` },
    { key: "search", label: `🔍 ${t("cycleBot.dataView.tabSearch")}` },
  ];

  return (
    <div className="cb-view cb-view--active cb-data-theme">
      <div className="cb-view-head">
        <div>
          <h1>📊 {t("cycleBot.dataView.title")}</h1>
          <p className="cb-view-sub">{t("cycleBot.dataView.subtitle")}</p>
        </div>
      </div>

      <div className="cb-tabs">
        {tabs.map((item) => (
          <button
            key={item.key}
            className={`cb-tab ${tab === item.key ? "active" : ""}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "alerts" && <AlertsList alerts={alerts} t={t} />}
      {tab === "cycles" && <CyclesList cycles={cycles} t={t} locale={locale} />}
      {tab === "search" && <SearchPanel onSearch={onSearch} results={searchResults} t={t} locale={locale} />}
    </div>
  );
}
